import type {
  MarkTeamConversationReadResponse,
  ProviderPresenceInfo,
  TeamConversationCreate,
  TeamConversationDetail,
  TeamConversationSummary,
  TeamMessageSummary,
  TeamReaction,
  TeamUnreadCounts,
} from "@/types/team-chat";

import { requestData } from "@/api/api-config";

const basePath = "team-chat";

export async function getConversations(cursor?: string, hidden?: boolean) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (hidden) params.set("hidden", "true");
  const qs = params.toString();
  return requestData<{
    data: TeamConversationSummary[];
    nextCursor: string | null;
  }>(`${basePath}/conversations${qs ? `?${qs}` : ""}`);
}

export async function createConversation(data: TeamConversationCreate) {
  return requestData<TeamConversationDetail>(
    `${basePath}/conversations`,
    "post",
    {
      json: data,
    },
  );
}

export async function getConversation(id: string) {
  return requestData<TeamConversationDetail>(`${basePath}/conversations/${id}`);
}

export async function getMessages(conversationId: string, cursor?: string) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  return requestData<{
    data: TeamMessageSummary[];
    nextCursor: string | null;
  }>(
    `${basePath}/conversations/${conversationId}/messages${qs ? `?${qs}` : ""}`,
  );
}

export async function sendMessage(
  conversationId: string,
  content: string,
  files?: File[],
) {
  const formData = new FormData();
  if (content) formData.append("content", content);
  files?.forEach((file) => formData.append("files", file));
  return requestData<TeamMessageSummary>(
    `${basePath}/conversations/${conversationId}/messages`,
    "post",
    { body: formData },
    true,
    60000,
  );
}

export async function getOnlineProviders() {
  return requestData<{ providers: ProviderPresenceInfo[] }>(
    `${basePath}/online-providers`,
  );
}

export async function markAsRead(conversationId: string) {
  return requestData<MarkTeamConversationReadResponse>(
    `${basePath}/conversations/${conversationId}/read`,
    "put",
  );
}

export async function editMessage(
  conversationId: string,
  messageId: string,
  content: string,
) {
  return requestData<TeamMessageSummary>(
    `${basePath}/conversations/${conversationId}/messages/${messageId}`,
    "patch",
    { json: { content } },
  );
}

export async function getUnreadCounts() {
  return requestData<TeamUnreadCounts>(`${basePath}/unread-counts`);
}

export async function pinConversation(conversationId: string) {
  return requestData<void>(
    `${basePath}/conversations/${conversationId}/pin`,
    "put",
  );
}

export async function unpinConversation(conversationId: string) {
  return requestData<void>(
    `${basePath}/conversations/${conversationId}/pin`,
    "delete",
  );
}

export async function hideConversation(conversationId: string) {
  return requestData<void>(
    `${basePath}/conversations/${conversationId}/hide`,
    "put",
  );
}

export async function unhideConversation(conversationId: string) {
  return requestData<void>(
    `${basePath}/conversations/${conversationId}/hide`,
    "delete",
  );
}

export async function updateConversation(
  conversationId: string,
  data: { name?: string },
) {
  return requestData<TeamConversationDetail>(
    `${basePath}/conversations/${conversationId}`,
    "patch",
    { json: data },
  );
}

export async function addMember(conversationId: string, providerId: string) {
  return requestData<TeamConversationDetail>(
    `${basePath}/conversations/${conversationId}/members`,
    "post",
    { json: { providerId } },
  );
}

export async function leaveConversation(conversationId: string) {
  return requestData<void>(
    `${basePath}/conversations/${conversationId}/leave`,
    "delete",
  );
}

export async function updatePresence(data: {
  customStatusText: string | null;
  customStatusEmoji: string | null;
  customStatusExpiresAt: string | null;
}) {
  return requestData<{
    customStatusText: string | null;
    customStatusEmoji: string | null;
    customStatusExpiresAt: string | null;
  }>(`${basePath}/presence`, "put", { json: data });
}

export async function addReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
) {
  return requestData<{ reactions: TeamReaction[] }>(
    `${basePath}/conversations/${conversationId}/messages/${messageId}/reactions`,
    "post",
    { json: { emoji } },
  );
}

export async function removeReaction(
  conversationId: string,
  messageId: string,
  emoji: string,
) {
  return requestData<{ reactions: TeamReaction[] }>(
    `${basePath}/conversations/${conversationId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    "delete",
  );
}
